import filter from 'lodash/filter';
import map from 'lodash/map';
import times from 'lodash/times';
import { useMemo } from 'react';

import type { ChatSummary } from './use-chats';

export type ChatGroup = {
  label: string;
  chats: ChatSummary[];
};

const GROUP_LABELS = [
  'Today',
  'Yesterday',
  'Previous 7 Days',
  'Previous 30 Days',
  'Older',
] as const;

function getGroupIndex (updatedAt: string, now: Date): number {
  const date = new Date(updatedAt);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = startOfToday.getTime() - date.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (date >= startOfToday) return 0; // Today
  if (diffDays < 1) return 1; // Yesterday
  if (diffDays < 7) return 2; // Previous 7 Days
  if (diffDays < 30) return 3; // Previous 30 Days
  return 4; // Older
}

export function useChatGroups (chats: ChatSummary[] | undefined): ChatGroup[] {
  return useMemo(() => {
    if (!chats || chats.length === 0) return [];

    const now = new Date();
    const buckets: ChatSummary[][] = times(GROUP_LABELS.length, () => []);

    for (const chat of chats) {
      const idx = getGroupIndex(chat.updated_at, now);
      buckets[idx].push(chat);
    }

    // Only return non-empty groups
    return filter(
      map(buckets, (bucket, i) => ({ label: GROUP_LABELS[i], chats: bucket })),
      (g) => g.chats.length > 0,
    );
  }, [chats]);
}
